import { useFetcherService, FetcherService, URLParamsBuilder } from 'shared/http';
import { Pageable } from 'shared/table/models/Pageable';
import { SearchAccountsTotalsRequest } from 'core/cuenta/models/SearchAccountTotalsRequest';
import { SearchCardTotalsRequest } from 'core/tarjeta/models/requests/SearchCardTotalsRequest';
import { AccountTotals } from 'core/cuenta/models/AccountTotals';
import { CardsTotals } from 'core/tarjeta/models/CardsTotals';
import { RefundRequest } from 'core/cuenta/models/requests/RefundRequest';
import { SearchTransfersRequest } from 'core/models/SearchTransfersRequest';
import { FileResponse } from 'core/models/data/FileResponse';
import { RefundData } from 'core/cuenta/models/RefundData';
import { Transferencia } from 'core/cuenta/models/Transferencia';
import { Cuenta } from 'core/cuenta/models/Cuenta';
import { DistribucionDeFondosRequest } from 'core/cuenta/models/requests/DistribucionDeFondosRequest';
import { FundsDistributionData } from 'core/cuenta/models/FundsDistributionData';
import { CrearCentroCostoRequest } from 'core/cuenta/models/requests/CrearCentroCostoRequest';
import { CentroCosto } from 'core/centrocosto/models/CentroCosto';
import { AccountBalance } from '../cuenta/models/AccountBalance';
import { Tarjeta } from '../tarjeta/models/Tarjeta';
import { SearchCentroCostoRequest } from 'core/centrocosto/models/SearchCentroCostoRequest';
import { SearchTarjetaRequest } from 'core/tarjeta/models/SearchTarjetaRequest';
import { SearchCuentasRequest } from 'core/cuenta/models/SearchCuentasRequests';
import { PagedResponseWithAggregation } from 'shared/table/models/PagedResponseWithAggregation';
import { SearchFiltrosMovimientosRequest } from 'core/movimientos/models/SearchFiltrosMovimientosRequest';
import { FiltrosMovimientos } from 'core/movimientos/models/FiltrosMovimientos';
import { Movimiento } from 'core/tarjeta/models/Movimiento';
import { SearchMovimientosRequest } from 'core/movimientos/models/SearchMovimientosRequest';
import { TotalesPorCuenta } from 'core/movimientos/models/TotalesPorCuenta';
import { TotalesPorTarjeta } from 'core/movimientos/models/TotalesPorTarjeta';
import { SearchTotalesMovimientosAgrupadosRequest } from 'core/movimientos/models/SearchTotalesMovimientosAgrupadosRequest';
import {
  GenerateReportTarjetaRequest,
  GenerateReportCentroCostoRequest,
  GenerateReporteMovimientosLiquidadosRequest,
} from '../reporte/models/ReportModels';
import { FilePayload } from 'shared/models/FilePayload';
class CoreService extends FetcherService {
  async getAllTarjetas(params?: SearchTarjetaRequest): Promise<Pageable<Tarjeta>> {
    const url = new URLParamsBuilder('tarjetas')
      .addQueryParams({
        centroCostoOrigenId: params?.centroCostoOrigenId,
        numeroCuenta: params?.numeroCuenta,
        numeroTarjeta: params?.numeroTarjeta,
        denominacionTarjeta: params?.denominacionTarjeta,
        page: params?.page,
        size: params?.size,
        cuentasHuerfanas: params?.cuentasHuerfanas,
        conSaldo: params?.conSaldo,
        mes: params?.periodo?.mes,
        anio: params?.periodo?.anio,
      })
      .build();
    const response = await this.get<Pageable<any>>(url);
    const pageableResponse = response.body;

    const mappedContent: Tarjeta[] = pageableResponse.content.map((apiCard: any) => {
      return new Tarjeta({
        id: apiCard.id,
        numero: apiCard.numeroTarjeta,
        denominacion: apiCard.denominacionTarjeta,
        numeroCuenta: apiCard.numeroCuenta,
        centroCostoAsociado: apiCard.centroCostoAsociado,
      });
    });

    return {
      ...pageableResponse,
      content: mappedContent,
    };
  }

  async getAccountSaldo(nroCuenta: number) {
    return this.get<AccountBalance>(`cuentas/${nroCuenta}`);
  }

  async searchAccountsTotals(request?: SearchAccountsTotalsRequest) {
    return this.get<PagedResponseWithAggregation<AccountTotals>>('cuentas/totales', {
      numeroCuenta: request?.numeroCuenta,
      anio: request?.anio || null,
      mes: request?.mes || null,
      page: request?.page || null,
      size: request?.size || null,
    }).then((r) => r.body);
  }

  async searchCardTotals(request: SearchCardTotalsRequest) {
    return this.get<Pageable<CardsTotals>>(`tarjetas/totales`, {
      numeroCuenta: request?.numeroCuenta,
      anio: request?.anio,
      mes: request?.mes,
      page: request?.page,
      size: request?.size,
    });
  }

  async getVisibleAccounts(request: SearchCuentasRequest): Promise<Cuenta[]> {
    return this.get<Cuenta[]>('cuentas', {
      centroCostoOrigenId: request?.centroCostoOrigenId,
      visibilidadCuenta: request?.visibilidad,
      tipoCuenta: request?.tipoCuenta,
      anio: request?.periodo?.anio,
      mes: request?.periodo?.mes,
      cuentasHuerfanas: request?.cuentasHuerfanas,
    }).then((r) => r.body.map((cuenta) => new Cuenta(cuenta)));
  }

  async getUnnasignedAccounts(): Promise<Cuenta[]> {
    return this.get<Cuenta[]>(`cuentas/sinasignar`).then((r) =>
      r.body.map((cuenta) => new Cuenta(cuenta)),
    );
  }

  async createCostCentre(data: CrearCentroCostoRequest) {
    return this.post<void>('centroscosto', data);
  }

  async getCostCentres(
    request?: SearchCentroCostoRequest,
  ): Promise<Pageable<CentroCosto>> {
    const response = await this.get<Pageable<CentroCosto>>(`centroscosto`, {
      numeroCuentaOrigen: request?.numeroCuentaOrigen,
      numeroCuenta: request?.numeroCuenta,
      denominacion: request?.denominacion,
      centroCostoPadreId: request?.centroCostoPadreId,
      cuentasHuerfanas: request?.cuentasHuerfanas,
      conSaldo: request?.conSaldo ?? null,
      centroCostosHojas: request?.centroCostosHojas,
      anio: request?.periodo?.anio,
      mes: request?.periodo?.mes,
      page: request?.page,
      size: request?.size,
    });
    return response.body;
  }

  async getCostCentre(id: string) {
    return this.get<CentroCosto>(`centroscosto/${id}`).then((r) => r.body);
  }

  async updateCostCentre(request: CrearCentroCostoRequest) {
    return this.put<void>(`centroscosto/${request.id}`, request);
  }

  async moveCostCentre(id: number, request: { centroCostoPadreId: number }) {
    return this.put<void>(`centroscosto/mover/${id}`, request);
  }

  async deleteCostCentre(idCostCentre: number) {
    return this.del<void>(`centroscosto/${idCostCentre}`);
  }

  async getMassDistributionTemplate(request: any) {
    return this.get<FileResponse>(`centroscosto/listado/template`, request, true);
  }

  async saveFundsDistribution(
    request: DistribucionDeFondosRequest,
    numeroCuenta: string,
  ): Promise<Transferencia[]> {
    request.numeroCuenta = numeroCuenta;
    return this.post<Transferencia[]>(`cuentas/transferencias`, request).then(
      (r) => r.body,
    );
  }

  async getFundsDistributionData(cuentaOrigen?: string): Promise<FundsDistributionData> {
    const url = cuentaOrigen ? `?cuentaOrigen=${cuentaOrigen}` : '';
    return this.get<FundsDistributionData>(`cuentas/distribucion${url}`).then((r) => ({
      destinos: r.body?.destinos.map((cuenta) => new Cuenta(cuenta)),
      origen: new Cuenta(r.body?.origen),
    }));
  }

  async getCostCentreRefund(): Promise<RefundData> {
    return this.get<RefundData>(`cuentas/devolucion`).then((r) => r.body);
  }

  async saveCostCentreRefund(request: RefundRequest): Promise<Transferencia> {
    return this.post<Transferencia>(`cuentas/transferencias/devolucion`, request).then(
      (r) => r.body,
    );
  }

  async getCostCentresChildren(): Promise<CentroCosto[]> {
    return this.get<CentroCosto[]>('centroscosto/hijos').then((response) => {
      if (!response.body || !Array.isArray(response.body)) {
        return [];
      }
      return response.body;
    });
  }

  async associateCardToCostCenter(numeroCuentaTarjeta: number, idCentroCosto: number) {
    return this.post<void>(`tarjetas/${numeroCuentaTarjeta}/asociar/${idCentroCosto}`);
  }

  async disassociateCard(idTarjeta: number) {
    return this.patch<void>(`tarjetas/${idTarjeta}/desasociar`);
  }

  async findTransferencias(
    request: SearchTransfersRequest,
  ): Promise<Pageable<Transferencia>> {
    return this.get<Pageable<Transferencia>>(`cuentas/transferencias`, {
      cuentaOrigen: request.cuentaOrigen,
      cuentaDestino: request.cuentaDestino,
      from: request.getFromDateString(),
      to: request.getToDateString(),
      page: request.page,
      size: request.size,
      estado: request.estado,
      username: request.username,
    }).then((r) => r.body);
  }

  async downloadTransferComprobante(transferId: number) {
    return this.get<FilePayload>(
      `cuentas/transferencias/${transferId}/comprobante`,
      undefined,
      true, // <- archivo
    ).then((r) => r.body);
  }

  async findMovimientos(
    request: SearchMovimientosRequest,
  ): Promise<Pageable<Movimiento>> {
    return this.get<Pageable<Movimiento>>(`movimientos`, {
      ...request,
      page: request.page || 0,
      size: request.size || 15,
    }).then((r) => r.body);
  }

  async getFiltrosMovimientos(
    request: SearchFiltrosMovimientosRequest,
  ): Promise<FiltrosMovimientos> {
    return this.get<FiltrosMovimientos>(`movimientos/filtros`, {
      mes: request.mes,
      anio: request.anio,
      buscarPor: request.buscarPor,
      numeroCuenta: request.numeroCuenta,
      idTarjeta: request.idTarjeta,
    }).then((r) => r.body);
  }

  async getTotalesMovimientosAgrupados(
    request: SearchTotalesMovimientosAgrupadosRequest,
  ): Promise<PagedResponseWithAggregation<TotalesPorCuenta | TotalesPorTarjeta>> {
    return this.get<PagedResponseWithAggregation<TotalesPorCuenta | TotalesPorTarjeta>>(
      `movimientos/totales`,
      {
        mes: request.mes,
        anio: request.anio,
        agruparPor: request.agruparPor,
        centroCostoPadreId: request.centroCostoPadreId,
        numeroCuenta: request.numeroCuenta,
        idTarjeta: request.idTarjeta,
        page: request.page || 0,
        size: request.size || 15,
      },
    ).then((r) => r.body);
  }

  async getReporteMovimientosLiquidados(
    request: GenerateReporteMovimientosLiquidadosRequest,
  ): Promise<FilePayload> {
    return this.get<FilePayload>(
      `reportes/movimientos-liquidados`,
      {
        from: request.from.toISOString().split('T')[0],
        to: request.to.toISOString().split('T')[0],
      },
      true,
    ).then((r) => r.body);
  }
}

const coreService = new CoreService();

export function useCoreService() {
  const { registerFetcherService } = useFetcherService();

  registerFetcherService(coreService);

  return coreService;
}

export const useReporteService = () => {
  const coreService = useCoreService();

  const generateMovimientosPorTarjetaReport = async (
    request: GenerateReportTarjetaRequest,
  ) => {
    // Reutilizamos el método `get` del servicio principal, que ya maneja la autenticación y el refresco de tokens.
    // El tercer parámetro `true` indica que esperamos un archivo como respuesta.
    const response = await coreService.get<FilePayload>(
      `reportes/movimientos-por-tarjeta`,
      request,
      true,
    );

    // Retornamos el objeto con el archivo y su nombre
    return response.body;
  };

  const generateRendicionPorCentroDeCostoReport = async (
    request: GenerateReportCentroCostoRequest,
  ) => {
    const response = await coreService.get<FilePayload>(
      `reportes/rendicion-por-centro-de-costo`,
      request,
      true,
    );
    return response.body;
  };

  const listarUltimasLiquidaciones = async (numeroCuenta: number) => {
    const response = await coreService.get<any[]>(`reportes/ultimas-liquidaciones`, {
      numeroCuenta,
    });
    return response.body;
  };

  const descargarUltimaLiquidacionPdf = async (
    numeroCuenta: number,
    keyValue: string,
  ) => {
    const response = await coreService.get<FilePayload>(
      `reportes/ultimas-liquidaciones/descarga`,
      { numeroCuenta, keyValue },
      true,
    );
    return response.body;
  };

  const descargarUltimasLiquidacionesZip = async (
    numeroCuenta: number,
    keyValues: string[],
  ) => {
    const response = await coreService.get<FilePayload>(
      `reportes/ultimas-liquidaciones/descarga-masiva`,
      { numeroCuenta, keyValue: keyValues },
      true,
    );
    return response.body;
  };

  return {
    generateMovimientosPorTarjetaReport,
    generateRendicionPorCentroDeCostoReport,
    listarUltimasLiquidaciones,
    descargarUltimaLiquidacionPdf,
    descargarUltimasLiquidacionesZip,
  };
};
